<ng-container *transloco="let t">
  <div id="mfa-card" class="bg-white w-full mx-auto" [class]="embedded() ? '' : 'p-6 sm:p-10 rounded-2xl shadow-card'">
    @if (showLogo()) {
      <div id="mfa-logo" class="flex justify-center mb-4">
        <img [src]="logo" [alt]="logoAlt" class="h-8 sm:h-10" />
      </div>
    }

    @if (showTitle()) {
      <div id="mfa-title-container" class="flex items-center gap-2 mb-3 sm:mb-4" [class.justify-center]="!leftAlign()">
        @if (showLockIcon()) {
          <mat-icon id="mfa-lock-icon" class="text-ld-neutral-500 text-xl!">lock</mat-icon>
        }
        <h1
          id="mfa-title"
          class="font-dm text-xl sm:text-2xl font-semibold"
          [class.text-center]="!leftAlign()"
          [class.text-ld-primary]="!leftAlign()"
          [class.text-ld-neutral]="leftAlign()"
        >
          {{ title() }}
        </h1>
      </div>
    }

    <p id="mfa-description" class="font-dm text-sm text-ld-neutral-500 mb-6" [class.text-center]="!leftAlign()">
      {{ displayDescription() }}
    </p>

    <div id="mfa-options" class="mfa-options-grid">
      @if (!hideEmailOption() && emailOptions().length > 0) {
        <div class="mfa-option-row">
          <mat-form-field appearance="outline" class="mfa-dropdown">
            <mat-label>{{ t('mfaMethodsEmail') | titlecase }}</mat-label>
            <mat-select
              [value]="initialEmail()"
              [disabled]="anyLoading()"
              (selectionChange)="selectedEmail.set($event.value)"
            >
              @for (option of emailOptions(); track option.value) {
                <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button
            id="mfa-email-button"
            type="button"
            class="btn-primary-ld-purple mfa-action-btn"
            [disabled]="anyLoading()"
            [attr.aria-busy]="emailLoading()"
            (click)="onSelectEmail(selectedEmail() || initialEmail()!)"
          >
            @if (emailLoading()) {
              <span
                class="mfa-btn-spinner animate-spin inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                role="status"
                aria-hidden="true"
              ></span>
            } @else {
              <span class="mfa-btn-icon">
                <img [src]="mailIcon" width="20" height="20" alt="" aria-hidden="true" />
              </span>
              <span class="mfa-btn-label">{{ t('mfaReceiveViaEmail') | uppercase }}</span>
            }
          </button>
        </div>
      }

      @if (textPhoneOptions().length > 0) {
        <div class="mfa-option-row">
          <mat-form-field appearance="outline" class="mfa-dropdown">
            <mat-label>{{ t('commonFieldsPhoneNumber') }}</mat-label>
            <mat-select
              [value]="initialPhone()"
              [disabled]="anyLoading()"
              (selectionChange)="selectedPhone.set($event.value)"
            >
              @for (option of textPhoneOptions(); track option.value) {
                <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button
            id="mfa-text-button"
            type="button"
            class="btn-primary-ld-purple mfa-action-btn"
            [disabled]="anyLoading()"
            [attr.aria-busy]="textLoading()"
            (click)="onSelectTextPhone(selectedPhone() || initialPhone()!)"
          >
            @if (textLoading()) {
              <span
                class="mfa-btn-spinner animate-spin inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                role="status"
                aria-hidden="true"
              ></span>
            } @else {
              <span class="mfa-btn-icon">
                <img [src]="textIcon" width="20" height="20" alt="" aria-hidden="true" />
              </span>
              <span class="mfa-btn-label">{{ t('mfaReceiveViaText') | uppercase }}</span>
            }
          </button>
        </div>
      }

      @if (callPhoneOptions().length > 0) {
        <div class="mfa-option-row">
          <mat-form-field appearance="outline" class="mfa-dropdown">
            <mat-label>{{ t('commonFieldsPhoneNumber') }}</mat-label>
            <mat-select
              [value]="initialCallPhone()"
              [disabled]="anyLoading()"
              (selectionChange)="selectedCallPhone.set($event.value)"
            >
              @for (option of callPhoneOptions(); track option.value) {
                <mat-option [value]="option">{{ option.masked || option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button
            id="mfa-call-button"
            type="button"
            class="btn-primary-ld-purple mfa-action-btn"
            [disabled]="anyLoading()"
            [attr.aria-busy]="callLoading()"
            (click)="onSelectCallPhone(selectedCallPhone() || initialCallPhone()!)"
          >
            @if (callLoading()) {
              <span
                class="mfa-btn-spinner animate-spin inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                role="status"
                aria-hidden="true"
              ></span>
            } @else {
              <span class="mfa-btn-icon">
                <img [src]="phoneIcon" width="20" height="20" alt="" aria-hidden="true" />
              </span>
              <span class="mfa-btn-label">{{ t('mfaReceiveViaCall') | uppercase }}</span>
            }
          </button>
        </div>
      }
    </div>

    @if (showMfaInfoIcon()) {
      <div id="mfa-info-container" class="flex items-center mt-6 justify-between">
        <button
          id="mfa-info-link"
          type="button"
          class="inline-flex items-center gap-1 cursor-pointer font-dm text-sm text-ld-neutral-500 bg-transparent border-0 p-0"
          [matTooltip]="isMobile() ? '' : mfaInfoTooltip()"
          matTooltipPosition="above"
          matTooltipClass="mfa-info-tooltip"
          (click)="isMobile() ? openMfaInfoSheet() : null"
        >
          <mat-icon class="text-base! w-4! h-4! text-ld-link!">info_outline</mat-icon>
          {{ t('mfaWhatIsCodeHeading') }}
        </button>
        <button
          id="mfa-cancel-button"
          type="button"
          class="bg-transparent border-0 text-ld-neutral-700 cursor-pointer p-0 font-dm text-sm font-bold uppercase tracking-wide hover:text-ld-neutral-900 focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded"
          (click)="onCancel()"
        >
          {{ t('commonButtonsCancel') }}
        </button>
      </div>
    }

    @if (showFaqLink()) {
      <p id="mfa-faq-text" class="text-left font-dm text-xs text-ld-neutral-500 mt-6">
        {{ t('mfaFaqText1') }}
        <a
          id="mfa-faq-link"
          [routerLink]="faqLink()"
          target="_blank"
          class="text-ld-primary underline cursor-pointer hover:text-ld-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded"
        >
          {{ t('mfaFaqLink') }}
        </a>
        {{ t('mfaFaqText2') }}
      </p>
    }

    @if (!showMfaInfoIcon()) {
      <div id="mfa-cancel-container" class="flex justify-end mt-6">
        <button
          id="mfa-cancel-button-bottom"
          type="button"
          class="bg-transparent border-0 text-ld-neutral-700 cursor-pointer p-0 font-dm text-sm font-bold uppercase tracking-wide hover:text-ld-neutral-900 focus-visible:outline-2 focus-visible:outline-ld-primary focus-visible:outline-offset-2 focus-visible:rounded"
          (click)="onCancel()"
        >
          {{ t('commonButtonsCancel') }}
        </button>
      </div>
    }

    @if (showMfaInfoSheet()) {
      <div
        id="mfa-info-sheet-backdrop"
        class="fixed inset-0 bg-black/50 z-[60] flex items-end"
        role="dialog"
        aria-modal="true"
        (click)="closeMfaInfoSheet()"
        (keydown.escape)="closeMfaInfoSheet()"
      >
        <div
          id="mfa-info-sheet"
          class="w-full bg-white rounded-t-2xl mfa-info-sheet-slide-up max-h-[80vh] flex flex-col"
          style="padding-bottom: env(safe-area-inset-bottom, 0px)"
          role="document"
          (click)="$event.stopPropagation()"
          (keydown.escape)="closeMfaInfoSheet()"
        >
          <div class="flex items-center justify-between px-4 py-3 bg-black rounded-t-2xl shrink-0">
            <span class="font-dm text-sm sm:text-base font-semibold text-white">
              {{ t('mfaWhatIsCodeHeading') }}
            </span>
            <button
              id="mfa-info-sheet-close-header"
              type="button"
              class="flex items-center justify-center size-8 bg-transparent border-none cursor-pointer text-white"
              [attr.aria-label]="t('commonAriaClose')"
              (click)="closeMfaInfoSheet()"
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <div class="overflow-y-auto flex-1 px-5 py-4">
            <p class="font-dm text-sm text-ld-neutral-700 leading-relaxed">
              {{ mfaInfoTooltip() }}
            </p>
          </div>

          <div class="px-5 pb-5 pt-2 shrink-0">
            <button
              id="mfa-info-sheet-close-btn"
              type="button"
              class="w-full inline-flex items-center justify-center bg-black text-white font-dm text-sm font-bold tracking-[0.5px] rounded-[20px] h-11 px-4 hover:bg-ld-neutral-700 cursor-pointer uppercase transition-colors"
              (click)="closeMfaInfoSheet()"
            >
              {{ t('commonAriaClose') }}
            </button>
          </div>
        </div>
      </div>
    }
  </div>
</ng-container>
